import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Alert } from '../components/ui/Alert';
import { CampaignStatusBadge } from '../components/CampaignStatusBadge';
import { MockBadge } from '../components/MockBadge';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime, formatNumber, formatSendTime, formatTime } from '../lib/format';
import { TOTP_LOCKED_MESSAGE, isTotpLocked } from '../lib/authErrors';
import {
  STEP_UP_ENDED_MESSAGE,
  isStepUpRequired,
  stepUpBody,
  stepUpCodeReady,
  useStepUpWindow,
} from '../lib/stepUp';
import { StepUpCodeField } from '../components/StepUpCodeField';
import { ApiError } from '../types/api';
import type { Campaign, HealthSummary, MessageCounts, PreflightResult } from '../types/api';

/** What prepare answers: the queued recipients and the id of the prepared version (the approval names it). */
interface PrepareResult {
  recipientCount: number;
  snapshotId: string;
}

/**
 * Everything that has left for SES. A message moves on from `sent` once SES
 * reports back (`delivered`, `bounced`, ...), so `sent` alone shrinks while
 * a send runs; same sum as core's `attemptedSends`.
 */
export function sentSoFar(counts: MessageCounts): number {
  return (
    counts.sent +
    (counts.delivered ?? 0) +
    (counts.bounced ?? 0) +
    (counts.complained ?? 0) +
    (counts.unsubscribed ?? 0)
  );
}

/** The draft was edited or prepared again after this page loaded it. */
function isCampaignChanged(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.body.error === 'campaign_changed';
}

/** A 409 from the API names the campaign's own state in plain words (e.g. "already approved") — safe to show as it is. */
function conflictMessage(err: unknown): string | null {
  return err instanceof ApiError && err.status === 409 && err.body.message
    ? err.body.message
    : null;
}

const CHECK_ICON: Record<string, string> = { ok: '✓', warn: '!', fail: '✗' };
const CHECK_TONE: Record<string, string> = {
  ok: 'text-success-text',
  warn: 'text-warning-text',
  fail: 'text-danger-text',
};

function PreflightList({ result }: { result: PreflightResult }) {
  return (
    <ul className="flex flex-col gap-2 list-none m-0 p-0 text-[13px]">
      {result.checks.map((check) => (
        <li
          key={check.id}
          className="flex gap-2.5 items-start px-3 py-2.5 border border-border rounded-[10px]"
        >
          <span className={CHECK_TONE[check.status]} aria-hidden="true">
            {CHECK_ICON[check.status]}
          </span>
          <div>
            <div className="font-medium">{check.title}</div>
            <div className="text-muted">{check.message}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function PreviewPane({ html }: { html: string }) {
  const [dark, setDark] = useState(false);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">Mobile preview (375px)</span>
        <Button
          variant="secondary"
          size="sm"
          className="ml-auto"
          onClick={() => setDark((d) => !d)}
        >
          {dark ? 'Show light' : 'Show dark'}
        </Button>
      </div>
      <div className="border border-border rounded-xl p-3 bg-bg flex justify-center">
        <iframe
          title="Mobile preview"
          sandbox=""
          srcDoc={`<html><head><meta name="color-scheme" content="${dark ? 'dark' : 'light'}"></head><body style="margin:0;background:${dark ? '#18181B' : '#fff'};color:${dark ? '#fff' : '#18181B'}">${html}</body></html>`}
          className="w-[375px] max-w-full h-[420px] border border-border rounded-lg bg-white"
        />
      </div>
    </div>
  );
}

/** The text/plain part recipients' mail apps use when they do not show HTML. Merge fields stay as they are; each recipient gets their own values. */
function PlainTextPane({ text }: { text: string }) {
  if (text.trim() === '') return null;
  return (
    <details className="border border-border rounded-xl bg-bg" data-testid="plain-text-preview">
      <summary className="cursor-pointer px-3 py-2.5 text-sm font-medium">
        Plain-text version
      </summary>
      <div className="px-3 pb-3 flex flex-col gap-2">
        <p className="text-xs text-muted m-0">
          Sent together with the HTML. Shown by mail apps that do not display HTML. Links are the
          same as in the HTML and are tracked the same way.
        </p>
        <pre className="m-0 max-h-[320px] overflow-auto whitespace-pre-wrap break-words text-[12px] leading-relaxed font-mono">
          {text}
        </pre>
      </div>
    </details>
  );
}

export function KampagneSendPage() {
  const { id } = useParams<{ id: string }>();
  const campaignId = id ?? '';
  const { isAdmin, isEditor, user } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['campaign', campaignId],
    queryFn: () => apiFetch<{ campaign: Campaign }>(`/campaigns/${campaignId}`),
    enabled: Boolean(campaignId),
    // The server moves an approved campaign on by itself (scheduled ->
    // sending -> sent), so keep reading it while it can still change.
    refetchInterval: (query) => {
      const status = query.state.data?.campaign.status;
      return status === 'scheduled' || status === 'sending' ? 5000 : false;
    },
  });
  const campaign = data?.campaign;
  // Only a draft can be prepared and approved. Once approved the campaign
  // is `scheduled` (waiting for its send time or the next minute), then
  // `sending`; the API refuses prepare/approve for all of these.
  const isDraft = campaign?.status === 'draft';
  const isScheduled = campaign?.status === 'scheduled';
  const isLive = campaign?.status === 'sending' || campaign?.status === 'paused';
  // A paused campaign is resumed in step 1 (same two checks as an approval).
  // A flow step is made by an automation: no test email and no typed count.
  const isPaused = campaign?.status === 'paused';
  const isFlowStep = campaign?.kind === 'flow_step';

  const preflightMutation = useMutation({
    mutationFn: () =>
      apiFetch<PreflightResult>(`/campaigns/${campaignId}/preflight`, { method: 'POST', body: {} }),
  });
  const runPreflight = preflightMutation.mutate;
  useEffect(() => {
    if (campaignId) runPreflight();
  }, [campaignId, runPreflight]);

  const prepareMutation = useMutation({
    mutationFn: () =>
      apiFetch<PrepareResult>(`/campaigns/${campaignId}/prepare`, { method: 'POST' }),
    // A prepare that found a change withdraws the test email: read the
    // campaign again so "Test email sent" is never shown for an older version.
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] }),
  });
  const runPrepare = prepareMutation.mutate;
  useEffect(() => {
    // Prepare is editor+ and draft-only in the API; never call it for a
    // viewer or for a campaign that is already approved.
    if (campaignId && isDraft && isEditor) runPrepare();
  }, [campaignId, isDraft, isEditor, runPrepare]);

  const [confirmCount, setConfirmCount] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const stepUp = useStepUpWindow();
  const codeBody = stepUp.active ? {} : stepUpBody(totpCode);
  const [approveError, setApproveError] = useState<string | null>(null);

  const approveMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/campaigns/${campaignId}/approve`, {
        method: 'POST',
        // `snapshotId` is the version this page shows: the API refuses
        // the approval if the campaign has changed since.
        body: {
          ...codeBody,
          expectedRecipientCount: recipientCount,
          snapshotId: prepareMutation.data?.snapshotId,
          // The send time this page shows. The API refuses the approval (409
          // campaign_changed) if an editor has changed it since.
          scheduledAt: campaign?.scheduledAt ?? null,
        },
      }),
    onSuccess: () => {
      if (!stepUp.active) stepUp.onStepUpSuccess();
      void queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] });
      setApproveError(null);
      // Nothing left to confirm: never keep a used code or count in the form.
      setConfirmCount('');
      setTotpCode('');
    },
    onError: (err) => {
      // The API's own words when it gives any (a 400 or 409 names the real
      // reason); only a wrong code comes back without one.
      setApproveError(
        isTotpLocked(err)
          ? TOTP_LOCKED_MESSAGE
          : isStepUpRequired(err)
            ? STEP_UP_ENDED_MESSAGE
            : err instanceof ApiError && err.body.message
              ? err.body.message
              : 'The code is wrong, or the request could not be completed.',
      );
      stepUp.onStepUpError(err);
      // A 409 means the campaign changed under us (e.g. another admin
      // approved it): show its real status.
      if (conflictMessage(err)) {
        void queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] });
      }
      // The campaign changed since this page loaded it: load the summary
      // (recipients, checks, preview) again and make the admin confirm anew.
      if (isCampaignChanged(err)) {
        setConfirmCount('');
        runPrepare();
        runPreflight();
      }
    },
  });

  const resumeMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/campaigns/${campaignId}/resume`, {
        method: 'POST',
        // A flow step names no count: its recipients keep arriving from the flow.
        body: isFlowStep ? codeBody : { ...codeBody, expectedRecipientCount: toSend },
      }),
    onSuccess: () => {
      if (!stepUp.active) stepUp.onStepUpSuccess();
      setApproveError(null);
      setConfirmCount('');
      setTotpCode('');
      void queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] });
      void queryClient.invalidateQueries({ queryKey: ['campaign-counts', campaignId] });
    },
    onError: (err) => {
      // The API's own words (a 409 names the real reason); a wrong code has none.
      setApproveError(
        isTotpLocked(err)
          ? TOTP_LOCKED_MESSAGE
          : isStepUpRequired(err)
            ? STEP_UP_ENDED_MESSAGE
            : err instanceof ApiError && err.body.message
              ? err.body.message
              : 'The code is wrong, or the request could not be completed.',
      );
      stepUp.onStepUpError(err);
      void queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] });
      void queryClient.invalidateQueries({ queryKey: ['campaign-counts', campaignId] });
    },
  });

  // Why a paused campaign cannot send: only the sender's pause is recorded
  // (campaigns keep no pause reason), so that is all the page can show.
  const healthQuery = useQuery({
    queryKey: ['health-summary'],
    queryFn: () => apiFetch<HealthSummary>('/health-summary'),
    enabled: Boolean(isPaused),
  });

  // The test email is the prepared version. It goes to the signed-in user,
  // so a version that changed can be tested again without leaving the page.
  const testSendMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/campaigns/${campaignId}/test-send`, {
        method: 'POST',
        body: { toEmail: user?.email },
      }),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] }),
  });

  const pauseMutation = useMutation({
    mutationFn: () => apiFetch(`/campaigns/${campaignId}/pause`, { method: 'POST' }),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] }),
  });

  const countsQuery = useQuery({
    queryKey: ['campaign-counts', campaignId],
    queryFn: () => apiFetch<{ counts: MessageCounts }>(`/campaigns/${campaignId}/counts`),
    enabled: Boolean(campaignId) && campaign !== undefined && !isDraft,
    refetchInterval: isLive ? 5000 : false,
  });

  // A draft shows what prepare just queued; an approved campaign is never
  // prepared again, so its number comes from its own messages.
  const recipientCount = isDraft
    ? (prepareMutation.data?.recipientCount ?? 0)
    : (countsQuery.data?.counts.total ?? 0);
  // What a resume still sends, counted by the server (never guessed here).
  const toSend = countsQuery.data?.counts.toSend ?? null;
  const senderPausedHere =
    healthQuery.data?.senderPaused.paused === true &&
    healthQuery.data.senderPaused.senderId === campaign?.senderId
      ? healthQuery.data.senderPaused
      : null;

  const logRef = useRef<{ at: number; text: string }[]>([]);
  const lastSentRef = useRef<number | null>(null);
  if (countsQuery.data) {
    const sent = sentSoFar(countsQuery.data.counts);
    if (lastSentRef.current !== null && sent !== lastSentRef.current) {
      logRef.current = [
        {
          at: Date.now(),
          text: `${formatNumber(sent - lastSentRef.current)} new sent · total ${formatNumber(sent)}`,
        },
        ...logRef.current,
      ].slice(0, 8);
    }
    lastSentRef.current = sent;
  }

  // A draft whose planned send time has already passed cannot be approved: the API
  // refuses it too (409 schedule_passed) instead of sending at once.
  const schedulePassed =
    isDraft && campaign?.scheduledAt != null && campaign.scheduledAt <= Date.now();
  const confirmMatches = confirmCount.trim() === String(recipientCount) && recipientCount > 0;
  const resumeCountMatches =
    isFlowStep || (toSend !== null && toSend > 0 && confirmCount.trim() === String(toSend));
  const canResume =
    senderPausedHere === null &&
    resumeCountMatches &&
    (stepUp.active || stepUpCodeReady(totpCode)) &&
    (isFlowStep || (toSend !== null && toSend > 0));
  // A planned send time that is still ahead is kept; otherwise the next
  // minute tick of the server sends.
  const sendsAtPlannedTime = campaign?.scheduledAt != null && campaign.scheduledAt > Date.now();
  // The API refuses a version that was never tested (409 test_send_missing).
  const testSent = Boolean(campaign?.testSendDoneAt);
  const canApprove =
    testSent &&
    !schedulePassed &&
    confirmMatches &&
    (stepUp.active || stepUpCodeReady(totpCode)) &&
    Boolean(prepareMutation.data?.snapshotId);

  if (isLoading) return <div className="text-muted">Loading campaign…</div>;
  if (!campaign) return <Alert>The campaign was not found.</Alert>;

  return (
    <>
      <header className="flex items-center gap-3">
        <Link to="/kampagner" className="no-underline">
          ← Campaigns
        </Link>
        <CampaignStatusBadge status={campaign.status} />
        <MockBadge />
      </header>

      <div className="flex gap-4 flex-grow max-[860px]:flex-col">
        <section
          aria-labelledby="approve-heading"
          className="flex-1 border border-border rounded-[14px] p-[22px] flex flex-col gap-4"
        >
          <div>
            <div className="text-link text-[13px] font-medium">Step 1 of 2</div>
            <h2 id="approve-heading" className="mt-1 text-xl font-semibold tracking-tight">
              {isPaused ? 'Resume' : 'Approve'} "{campaign.name}"
            </h2>
            <p className="mt-1.5 text-muted" data-testid="approve-lead">
              {isPaused
                ? isFlowStep
                  ? 'The flow keeps adding recipients while this is paused. Resuming sends everyone still waiting.'
                  : toSend === null
                    ? 'Counting who is still to be sent…'
                    : `${formatNumber(toSend)} people are still to be sent. Resuming sends them and cannot be recalled.`
                : (isDraft || isScheduled) && sendsAtPlannedTime
                  ? `Will be sent to ${formatNumber(recipientCount)} people at the planned time and cannot be recalled.`
                  : `Sent to ${formatNumber(recipientCount)} people and cannot be recalled.`}
            </p>
          </div>

          <div className="flex flex-col gap-2 text-[13px]">
            <div className="flex gap-2.5 items-center px-3 py-2.5 border border-border rounded-[10px]">
              <span
                className={campaign.testSendDoneAt ? 'text-success-text' : 'text-warning-text'}
                aria-hidden="true"
              >
                {campaign.testSendDoneAt ? '✓' : '!'}
              </span>
              <span>
                {campaign.testSendDoneAt
                  ? 'Test email sent'
                  : isDraft
                    ? 'No test email sent for this version yet'
                    : 'No test email sent yet'}
              </span>
              {isDraft && isEditor && !campaign.testSendDoneAt && user?.email ? (
                <Button
                  variant="secondary"
                  size="sm"
                  className="ml-auto"
                  disabled={testSendMutation.isPending || prepareMutation.isPending}
                  onClick={() => testSendMutation.mutate()}
                >
                  {testSendMutation.isPending ? 'Sending…' : `Send test to ${user.email}`}
                </Button>
              ) : null}
            </div>
            {testSendMutation.isError ? (
              <Alert>
                {testSendMutation.error instanceof ApiError && testSendMutation.error.body.message
                  ? testSendMutation.error.body.message
                  : 'The test email could not be sent.'}
              </Alert>
            ) : null}
            <div className="flex gap-2.5 items-center px-3 py-2.5 border border-border rounded-[10px]">
              <span className="text-success-text" aria-hidden="true">
                ✓
              </span>
              {isPaused
                ? isFlowStep
                  ? `${toSend === null ? '…' : formatNumber(toSend)} recipients waiting (the flow adds more)`
                  : `${toSend === null ? '…' : formatNumber(toSend)} recipients still to be sent`
                : `${formatNumber(recipientCount)} recipients`}
            </div>
            {campaign.coupon ? (
              <div
                className="flex gap-2.5 items-center px-3 py-2.5 border border-border rounded-[10px]"
                data-testid="coupon-row"
              >
                <span className="text-success-text" aria-hidden="true">
                  ✓
                </span>
                <span>
                  {`Personal discount code: ${campaign.coupon.percent} % off, valid ${campaign.coupon.validDays} ${campaign.coupon.validDays === 1 ? 'day' : 'days'}, codes like ${campaign.coupon.prefix}-7KQ4M2XC`}
                </span>
              </div>
            ) : null}
            {campaign.audience?.excludeOrderedSince != null ? (
              <div
                className="flex gap-2.5 items-center px-3 py-2.5 border border-border rounded-[10px]"
                data-testid="ordered-since-row"
              >
                <span className="text-success-text" aria-hidden="true">
                  ✓
                </span>
                <span>
                  {`Skips contacts who ordered since ${formatDateTime(campaign.audience.excludeOrderedSince)}, also orders placed after approval`}
                </span>
              </div>
            ) : null}
            {isDraft || isScheduled || isPaused ? (
              <div
                className="flex gap-2.5 items-center px-3 py-2.5 border border-border rounded-[10px]"
                data-testid="send-time-row"
              >
                <span
                  className={schedulePassed ? 'text-warning-text' : 'text-success-text'}
                  aria-hidden="true"
                >
                  {schedulePassed ? '!' : '✓'}
                </span>
                <span>
                  {isPaused
                    ? sendsAtPlannedTime
                      ? `Sends ${formatSendTime(campaign.scheduledAt as number)} if resumed before then`
                      : 'Sends within a minute of resuming'
                    : campaign.scheduledAt === null
                      ? isDraft
                        ? 'Sends right after approval'
                        : 'Sends within a minute of approval'
                      : schedulePassed
                        ? `The planned send time has passed (${formatSendTime(campaign.scheduledAt)}). An editor must pick a new time, or switch to "Right after approval", and the campaign must be reviewed again.`
                        : sendsAtPlannedTime
                          ? `Sends ${formatSendTime(campaign.scheduledAt)}`
                          : 'Sends within a minute'}
                </span>
              </div>
            ) : null}
          </div>

          <div>
            <h3 className="text-sm font-semibold mb-2">Checks before sending</h3>
            {preflightMutation.isPending ? (
              <div className="text-muted text-sm">Running checks…</div>
            ) : preflightMutation.data ? (
              <PreflightList result={preflightMutation.data} />
            ) : (
              <Button variant="secondary" size="sm" onClick={() => preflightMutation.mutate()}>
                Run checks
              </Button>
            )}
          </div>

          {preflightMutation.data ? (
            <>
              <PreviewPane html={preflightMutation.data.previews.html} />
              <PlainTextPane text={preflightMutation.data.previews.text} />
            </>
          ) : null}

          {isDraft || isPaused ? (
            <>
              {isPaused && isFlowStep ? null : (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="confirm-count">
                    Type {formatNumber(isPaused ? (toSend ?? 0) : recipientCount)} to confirm
                  </Label>
                  <Input
                    id="confirm-count"
                    value={confirmCount}
                    onChange={(e) => setConfirmCount(e.target.value)}
                    disabled={!isAdmin}
                  />
                  <div className="min-h-[16px] text-xs text-danger-text">
                    {confirmCount.length > 0 && !(isPaused ? resumeCountMatches : confirmMatches)
                      ? 'The number does not match.'
                      : ''}
                  </div>
                </div>
              )}
              <StepUpCodeField
                id="approve-totp"
                value={totpCode}
                onChange={setTotpCode}
                active={stepUp.active}
                minutesLeft={stepUp.minutesLeft}
                disabled={!isAdmin}
              />
            </>
          ) : null}

          {isPaused && senderPausedHere ? (
            <Alert>
              The sender is paused{senderPausedHere.reason ? `: ${senderPausedHere.reason}` : '.'}{' '}
              Resume the sender first (<Link to="/indstillinger/afsendere">Senders</Link>); until
              then this campaign would send nothing.
            </Alert>
          ) : null}
          {isPaused && healthQuery.data?.killSwitch.engaged ? (
            <Alert>
              The kill switch is on. Nothing is sent until it is switched off, also after resuming.
            </Alert>
          ) : null}
          {approveError ? <Alert>{approveError}</Alert> : null}
          {isDraft && isAdmin && !testSent ? (
            <p className="m-0 text-muted text-sm">
              Send the test email above first. Then you can approve.
            </p>
          ) : null}
          {prepareMutation.isError ? (
            <Alert>
              {conflictMessage(prepareMutation.error) ??
                'The recipients could not be prepared. Check the sender and content, and try again.'}
            </Alert>
          ) : null}

          <div className="flex gap-2.5 mt-auto">
            <Button asChild variant="secondary">
              <Link to="/kampagner">{isDraft || isPaused ? 'Cancel' : 'Back to campaigns'}</Link>
            </Button>
            {isPaused && isAdmin ? (
              <Button
                className="flex-grow"
                disabled={!canResume || resumeMutation.isPending}
                onClick={() => resumeMutation.mutate()}
              >
                {resumeMutation.isPending ? 'Resuming…' : 'Resume sending'}
              </Button>
            ) : isDraft && isAdmin ? (
              <Button
                className="flex-grow"
                disabled={!canApprove || approveMutation.isPending}
                onClick={() => approveMutation.mutate()}
              >
                {approveMutation.isPending
                  ? 'Approving…'
                  : campaign.scheduledAt !== null
                    ? 'Approve and schedule'
                    : 'Approve and send'}
              </Button>
            ) : (
              <div className="flex-grow flex items-center text-muted text-sm">
                {isPaused
                  ? 'Only an admin can resume a paused campaign.'
                  : !isDraft
                    ? 'This campaign has already been approved.'
                    : isEditor
                      ? 'Sent for approval — an admin must approve the send.'
                      : 'Only an admin can approve sending.'}
              </div>
            )}
          </div>
        </section>

        <section
          aria-labelledby="live-heading"
          className="flex-1 border border-border rounded-[14px] p-[22px] flex flex-col gap-[18px]"
        >
          <div className="flex items-start">
            <div>
              <div className="text-link text-[13px] font-medium">Step 2 of 2</div>
              <h2 id="live-heading" className="mt-1 text-xl font-semibold">
                {campaign.status === 'paused'
                  ? 'Paused'
                  : campaign.status === 'sending'
                    ? 'Sending now'
                    : campaign.status === 'sent'
                      ? 'Sent'
                      : campaign.status === 'failed'
                        ? 'Failed'
                        : isScheduled
                          ? campaign.scheduledAt !== null && campaign.scheduledAt > Date.now()
                            ? `Approved, sends ${formatSendTime(campaign.scheduledAt)}`
                            : 'Approved, waiting to send'
                          : 'Waiting'}
              </h2>
            </div>
          </div>

          {campaign.status === 'paused' && campaign.scheduledAt !== null ? (
            <div className="text-muted text-sm" data-testid="paused-schedule-note">
              {campaign.scheduledAt > Date.now()
                ? `The planned send time (${formatSendTime(campaign.scheduledAt)}) is kept. If the campaign is resumed before then, it waits for that time.`
                : `The planned send time (${formatSendTime(campaign.scheduledAt)}) passed while the campaign was paused. If it is resumed, sending starts within a minute.`}
            </div>
          ) : null}

          {isLive && countsQuery.data ? (
            <>
              <div aria-live="polite" className="flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-3.5">
                  <div>
                    <div className="text-muted text-xs">Sent</div>
                    <div className="text-lg font-semibold">
                      {formatNumber(sentSoFar(countsQuery.data.counts))} /{' '}
                      {formatNumber(
                        countsQuery.data.counts.total ?? sentSoFar(countsQuery.data.counts),
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted text-xs">Unclear</div>
                    <div className="text-lg font-semibold">
                      {formatNumber(countsQuery.data.counts.unknown)}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted text-xs">Retrying</div>
                    <div className="text-lg font-semibold">
                      {formatNumber(countsQuery.data.counts.retryable)}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted text-xs">Failed permanently</div>
                    <div className="text-lg font-semibold">
                      {formatNumber(countsQuery.data.counts.failed)}
                    </div>
                  </div>
                  {(countsQuery.data.counts.expired ?? 0) > 0 ? (
                    <div data-testid="expired-count">
                      <div className="text-muted text-xs">
                        Expired, not sent (waited over 48 h, or an earlier mail in the flow did)
                      </div>
                      <div className="text-lg font-semibold">
                        {formatNumber(countsQuery.data.counts.expired ?? 0)}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="font-mono text-xs text-muted flex flex-col gap-1.5 p-3 bg-[#FAFAFA] rounded-[10px] min-h-[80px]">
                {logRef.current.length === 0 ? (
                  <span>Waiting for the first updates…</span>
                ) : (
                  logRef.current.map((entry) => (
                    <span key={entry.at}>
                      {formatTime(entry.at)} {entry.text}
                    </span>
                  ))
                )}
              </div>
              {isAdmin && campaign.status === 'sending' ? (
                <div className="mt-auto flex justify-end gap-2.5">
                  <Button
                    variant="secondary"
                    disabled={pauseMutation.isPending}
                    onClick={() => pauseMutation.mutate()}
                  >
                    ❙❙ Pause this campaign
                  </Button>
                </div>
              ) : null}
            </>
          ) : isScheduled ? (
            <>
              <div className="text-muted text-sm" aria-live="polite">
                {campaign.scheduledAt !== null && campaign.scheduledAt > Date.now()
                  ? `Sending starts ${formatSendTime(campaign.scheduledAt)}.`
                  : 'Sending starts within a minute.'}{' '}
                Content and recipients are locked.
              </div>
              {isAdmin ? (
                <div className="mt-auto flex flex-col gap-2.5">
                  {pauseMutation.isError ? (
                    <Alert>
                      {conflictMessage(pauseMutation.error) ?? 'The campaign could not be paused.'}
                    </Alert>
                  ) : null}
                  <div className="flex justify-end">
                    <Button
                      variant="secondary"
                      disabled={pauseMutation.isPending}
                      onClick={() => pauseMutation.mutate()}
                    >
                      ❙❙ Pause this campaign
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <div className="text-muted text-sm">
              {campaign.status === 'sent'
                ? 'The campaign has been sent.'
                : campaign.status === 'failed'
                  ? 'The campaign failed and was not sent.'
                  : 'Sending starts within a minute of approval, or at the scheduled time.'}
            </div>
          )}
        </section>
      </div>
    </>
  );
}
